import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function RecruitmentDashboard({ user, onLogout }) {
  const [analytics, setAnalytics] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showJobForm, setShowJobForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobFormData, setJobFormData] = useState({
    title: '',
    department: '',
    location: '',
    job_type: 'Full-time',
    experience_required: '0-1 years',
    salary_range_min: '',
    salary_range_max: '',
    description: '',
    requirements: '',
    responsibilities: '',
    skills_required: '',
    openings: 1,
    application_deadline: ''
  });

  useEffect(() => {
    fetchAnalytics();
    fetchJobs();
    fetchApplications();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/recruitment-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/jobs`,
        {
          ...jobFormData,
          salary_range_min: parseFloat(jobFormData.salary_range_min) || null,
          salary_range_max: parseFloat(jobFormData.salary_range_max) || null,
          openings: parseInt(jobFormData.openings),
          requirements: jobFormData.requirements.split('\n').filter(r => r.trim()),
          responsibilities: jobFormData.responsibilities.split('\n').filter(r => r.trim()),
          skills_required: jobFormData.skills_required.split(',').map(s => s.trim()).filter(s => s)
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      alert('✅ Job posting created successfully!');
      setShowJobForm(false);
      setJobFormData({
        title: '', department: '', location: '', job_type: 'Full-time',
        experience_required: '0-1 years', salary_range_min: '', salary_range_max: '',
        description: '', requirements: '', responsibilities: '', skills_required: '',
        openings: 1, application_deadline: ''
      });
      fetchJobs();
      fetchAnalytics();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Open': 'bg-green-100 text-green-800',
      'Closed': 'bg-red-100 text-red-800',
      'On Hold': 'bg-yellow-100 text-yellow-800',
      'Applied': 'bg-blue-100 text-blue-800',
      'Screening': 'bg-purple-100 text-purple-800',
      'Shortlisted': 'bg-indigo-100 text-indigo-800',
      'Interview': 'bg-orange-100 text-orange-800',
      'Offered': 'bg-green-100 text-green-800',
      'Hired': 'bg-green-200 text-green-900',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Recruitment Dashboard</h1>
            <p className="mt-2 text-gray-600">Manage job postings, candidates, and hiring pipeline</p>
          </div>
          {!showJobForm && (
            <button
              onClick={() => setShowJobForm(true)}
              className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1a1f3a] transition"
            >
              📝 Post New Job
            </button>
          )}
        </div>

        {/* Analytics */}
        {analytics && activeTab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-100 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{analytics.jobs.open}</p>
                <p className="text-sm text-blue-600 mt-1">Open Jobs</p>
              </div>
              <div className="bg-green-100 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-green-700">{analytics.applications.total}</p>
                <p className="text-sm text-green-600 mt-1">Total Applications</p>
              </div>
              <div className="bg-purple-100 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-purple-700">{analytics.interviews.scheduled}</p>
                <p className="text-sm text-purple-600 mt-1">Scheduled Interviews</p>
              </div>
              <div className="bg-orange-100 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-orange-700">{analytics.offers.pending}</p>
                <p className="text-sm text-orange-600 mt-1">Pending Offers</p>
              </div>
              <div className="bg-pink-100 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-pink-700">{analytics.applications.by_status.Hired || 0}</p>
                <p className="text-sm text-pink-600 mt-1">Hired</p>
              </div>
            </div>

            {/* Pipeline Stats */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Hiring Pipeline</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(analytics.applications.by_status).map(([status, count]) => (
                  <div key={status} className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-sm text-gray-600">{status}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Job Form */}
        {showJobForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Create Job Posting</h2>
            <form onSubmit={handleCreateJob}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
                  <input
                    type="text"
                    required
                    value={jobFormData.title}
                    onChange={(e) => setJobFormData({ ...jobFormData, title: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
                  <input
                    type="text"
                    required
                    value={jobFormData.department}
                    onChange={(e) => setJobFormData({ ...jobFormData, department: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="e.g., Engineering"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                  <input
                    type="text"
                    required
                    value={jobFormData.location}
                    onChange={(e) => setJobFormData({ ...jobFormData, location: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="e.g., Bangalore, India"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Type *</label>
                  <select
                    required
                    value={jobFormData.job_type}
                    onChange={(e) => setJobFormData({ ...jobFormData, job_type: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Experience Required *</label>
                  <select
                    required
                    value={jobFormData.experience_required}
                    onChange={(e) => setJobFormData({ ...jobFormData, experience_required: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  >
                    <option value="0-1 years">0-1 years</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="5+ years">5+ years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Openings</label>
                  <input
                    type="number"
                    value={jobFormData.openings}
                    onChange={(e) => setJobFormData({ ...jobFormData, openings: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Salary Range (Min)</label>
                  <input
                    type="number"
                    value={jobFormData.salary_range_min}
                    onChange={(e) => setJobFormData({ ...jobFormData, salary_range_min: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="e.g., 500000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Salary Range (Max)</label>
                  <input
                    type="number"
                    value={jobFormData.salary_range_max}
                    onChange={(e) => setJobFormData({ ...jobFormData, salary_range_max: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="e.g., 1000000"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Description *</label>
                  <textarea
                    required
                    rows={4}
                    value={jobFormData.description}
                    onChange={(e) => setJobFormData({ ...jobFormData, description: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="Detailed job description"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requirements (one per line) *</label>
                  <textarea
                    required
                    rows={4}
                    value={jobFormData.requirements}
                    onChange={(e) => setJobFormData({ ...jobFormData, requirements: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="Bachelor's degree in Computer Science\n3+ years of experience"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Responsibilities (one per line) *</label>
                  <textarea
                    required
                    rows={4}
                    value={jobFormData.responsibilities}
                    onChange={(e) => setJobFormData({ ...jobFormData, responsibilities: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="Design and develop software\nCollaborate with teams"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Skills Required (comma-separated) *</label>
                  <input
                    type="text"
                    required
                    value={jobFormData.skills_required}
                    onChange={(e) => setJobFormData({ ...jobFormData, skills_required: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    placeholder="JavaScript, React, Node.js, Python"
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1a1f3a] transition disabled:opacity-50"
                >
                  {loading ? '⏳ Creating...' : '📝 Create Job'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowJobForm(false)}
                  className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition"
                >
                  ❌ Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'overview'
                  ? 'border-b-4 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📊 Overview
            </button>
            <button
              onClick={() => setActiveTab('jobs')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'jobs'
                  ? 'border-b-4 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              💼 Jobs ({jobs.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'applications'
                  ? 'border-b-4 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📝 Applications ({applications.length})
            </button>
          </div>
        </div>

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Job Postings</h2>
            
            {jobs.length === 0 ? (
              <p className="text-gray-500">No job postings yet.</p>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id} className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {job.department} • {job.location} • {job.job_type} • {job.experience_required}
                        </p>
                        <p className="text-sm text-gray-700 mt-2">{job.description.substring(0, 150)}...</p>
                        <div className="flex gap-2 mt-2">
                          {job.skills_required && job.skills_required.slice(0, 3).map((skill, i) => (
                            <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded text-xs font-semibold ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                        <p className="text-sm text-gray-600 mt-2">{job.openings} opening(s)</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Applications</h2>
            
            {applications.length === 0 ? (
              <p className="text-gray-500">No applications yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Candidate</th>
                      <th className="px-4 py-2 text-left">Job</th>
                      <th className="px-4 py-2 text-left">Applied</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Pipeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {app.candidate ? `${app.candidate.first_name} ${app.candidate.last_name}` : 'N/A'}
                        </td>
                        <td className="px-4 py-2">{app.job ? app.job.title : 'N/A'}</td>
                        <td className="px-4 py-2">
                          {new Date(app.applied_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(app.status)}`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">{app.pipeline_stage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default RecruitmentDashboard;